import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material";
import { CloudUpload, Preview, Upload } from "@mui/icons-material";
import { db } from "../firebase";
import { collection, getDocs, setDoc, doc } from "firebase/firestore";

function ProductImport({ open, onClose, onImportComplete, categories }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [previewMode, setPreviewMode] = useState(true);
  const [productsToImport, setProductsToImport] = useState([]);
  const [duplicateHandling, setDuplicateHandling] = useState("skip"); // "skip" or "overwrite"
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    newProducts: false,
    duplicateProducts: false,
    invalidProducts: false
  });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();
      
      if (
        fileType === "text/csv" ||
        fileName.endsWith(".csv") ||
        fileType === "application/json" ||
        fileName.endsWith(".json")
      ) {
        setFile(selectedFile);
        setError("");
        setResults(null);
        setProductsToImport([]);
        setPreviewMode(true);
        setConfirmationChecked(false);
      } else {
        setError("Please select a CSV or JSON file");
      }
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split(",").map(h => h.trim());
    
    return lines.slice(1).map((line, index) => {
      // Skip empty lines
      if (!line.trim()) return null;
      
      const values = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
      const obj = {};
      
      headers.forEach((header, headerIndex) => {
        let value = values[headerIndex] || "";
        
        if (header === "price") {
          obj[header] = parseFloat(value) || 0;
        } else if (header === "aisle" || header === "rack_level") {
          obj[header] = parseInt(value, 10) || 0;
        } else if (header === "keywords") {
          obj[header] = value ? value.split(";").map(k => k.trim()).filter(Boolean) : [];
        } else {
          obj[header] = value;
        }
      });
      
      return obj;
    }).filter(product => product !== null && product.name && product.name.trim() !== "");
  };

  const categoryIdByNameLower = React.useMemo(() => {
    const map = new Map();
    if (categories) {
      categories.forEach(cat => {
        map.set((cat.name || "").trim().toLowerCase(), cat.id);
      });
    }
    return map;
  }, [categories]);

  const normalizeCategoryId = (categoryValue) => {
    if (!categoryValue) return "";
    const value = categoryValue.toString().trim();
    
    if (categories?.find(cat => cat.id === value)) {
      return value;
    }
    
    return categoryIdByNameLower.get(value.toLowerCase()) || "";
  };

  const validateProduct = (product, index) => {
    const required = ["name", "price"];
    const missing = required.filter(field => 
      !product[field] && product[field] !== 0
    );
    
    if (missing.length > 0) {
      return `Missing required fields: ${missing.join(", ")}`;
    }
    
    if (isNaN(product.price) || product.price < 0) {
      return "price must be a non-negative number";
    }
    
    if (product.aisle && (isNaN(product.aisle) || product.aisle < 0)) {
      return "aisle must be a non-negative number";
    }
    
    if (product.rack_level && (isNaN(product.rack_level) || product.rack_level < 0)) {
      return "rack_level must be a non-negative number";
    }
    
    if (product.category && !normalizeCategoryId(product.category)) {
      return `category "${product.category}" not found. Available categories: ${categories?.map(c => c.name).join(", ")}`;
    }
    
    return null;
  };

  const generateCustomProductId = async () => {
    const snap = await getDocs(collection(db, "products"));
    const nums = snap.docs
      .map((d) => d.id.match(/^product(\d+)$/))
      .filter(Boolean)
      .map((m) => parseInt(m[1], 10))
      .sort((a, b) => a - b);
    return `product${nums.length ? nums[nums.length - 1] + 1 : 1}`;
  };

  const analyzeProducts = async (csvProducts) => {
    const existing = await getDocs(collection(db, "products"));
    const existingProducts = existing.docs.map(d => ({ id: d.id, ...d.data() }));
    
    const newProducts = [];
    const duplicateProducts = [];
    const invalidProducts = [];
    
    csvProducts.forEach((csvProduct, index) => {
      const validationError = validateProduct(csvProduct, index);
      
      if (validationError) {
        invalidProducts.push({
          product: csvProduct,
          error: validationError,
          rowIndex: index + 1
        });
        return;
      }
      
      // Normalize the product
      const normalizedProduct = {
        ...csvProduct,
        category_id: normalizeCategoryId(csvProduct.category || csvProduct.category_id || ""),
        keywords: Array.isArray(csvProduct.keywords) ? csvProduct.keywords : 
                 typeof csvProduct.keywords === 'string' ? csvProduct.keywords.split(";").map(k => k.trim()).filter(Boolean) : []
      };
      
      // Check for duplicates (name OR barcode match)
      const duplicate = existingProducts.find(existing => 
        existing.name?.toLowerCase().trim() === normalizedProduct.name?.toLowerCase().trim() ||
        (normalizedProduct.barcode && existing.barcode === normalizedProduct.barcode)
      );
      
      if (duplicate) {
        duplicateProducts.push({
          product: normalizedProduct,
          existingProduct: duplicate,
          rowIndex: index + 1
        });
      } else {
        newProducts.push({
          product: normalizedProduct,
          rowIndex: index + 1
        });
      }
    });
    
    return { newProducts, duplicateProducts, invalidProducts };
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const text = await file.text();
      let csvProducts = [];
      
      if (file.name.toLowerCase().endsWith(".json")) {
        csvProducts = JSON.parse(text);
      } else {
        csvProducts = parseCSV(text);
      }

      if (!Array.isArray(csvProducts)) {
        throw new Error("File must contain an array of products");
      }

      const { newProducts, duplicateProducts, invalidProducts } = await analyzeProducts(csvProducts);

      // Initially set to import only new products (will be updated when user chooses duplicate handling)
      setProductsToImport(newProducts);
      
      setResults({
        total: csvProducts.length,
        new: newProducts.length,
        duplicates: duplicateProducts.length,
        invalid: invalidProducts.length,
        newProducts,
        duplicateProducts,
        invalidProducts,
      });
      
      setPreviewMode(false);

    } catch (err) {
      setError(`Preview failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!confirmationChecked) {
      setError("Please confirm the import details");
      return;
    }

    if (productsToImport.length === 0) {
      setError("No products to import");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      let successCount = 0;
      let failedCount = 0;
      const failedProducts = [];

      for (const item of productsToImport) {
        try {
          let productId;
          
          // If it's a duplicate and we're overwriting, use existing ID
          if (item.existingProduct && duplicateHandling === "overwrite") {
            productId = item.existingProduct.id;
          } else {
            productId = await generateCustomProductId();
          }
          
          await setDoc(doc(db, "products", productId), {
            name: item.product.name,
            description: item.product.description || "",
            price: item.product.price,
            aisle: item.product.aisle || 0,
            rack_level: item.product.rack_level || 0,
            category_id: item.product.category_id,
            barcode: item.product.barcode || "",
            keywords: item.product.keywords,
            image_url: "",
            image_path: "",
          });
          
          successCount++;
        } catch (err) {
          failedCount++;
          failedProducts.push(`${item.product.name}: ${err.message}`);
        }
      }

      setResults({
        ...results,
        imported: successCount,
        importFailed: failedCount,
        importErrors: failedProducts,
        completed: true
      });

      if (successCount > 0) {
        onImportComplete?.();
      }

    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError("");
    setResults(null);
    setProductsToImport([]);
    setPreviewMode(true);
    setConfirmationChecked(false);
    setExpandedSections({
      newProducts: false,
      duplicateProducts: false,
      invalidProducts: false
    });
    onClose();
  };

  const handleDuplicateHandlingChange = (event) => {
    setDuplicateHandling(event.target.value);
    setConfirmationChecked(false);
    
    // Update products to import based on new selection
    if (results) {
      if (event.target.value === "skip") {
        setProductsToImport(results.newProducts);
      } else {
        setProductsToImport([...results.newProducts, ...results.duplicateProducts]);
      }
    }
  };

  const canProceedToImport = results && !results.completed && productsToImport.length > 0;
  const showImportResults = results && results.completed;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Upload />
        Import Products with Preview
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a CSV or JSON file with product data. Preview what will be imported before proceeding.
            Required fields: name, price. Optional: description, aisle, rack_level, category, barcode, keywords (semicolon-separated).
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontWeight: "bold" }}>
            CSV Example:
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontFamily: "monospace", backgroundColor: "#f5f5f5", p: 1, fontSize: "0.8rem" }}>
            name,description,price,aisle,rack_level,category,barcode,keywords<br/>
            "Apple Red",Fresh red apples,2.50,1,2,Fruits,1234567890,fresh;fruit;healthy<br/>
            "Bread White",Whole wheat bread,3.20,2,1,Bakery,9876543210,bread;bakery;wheat
          </Typography>
          
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUpload />}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            Choose File
            <input
              type="file"
              accept=".csv,.json"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </Button>
          
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {file.name}
            </Typography>
          )}
        </Box>


        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>
          </Alert>
        )}

        {results && !results.completed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Analysis Results:</Typography>
            <Typography variant="body2">• Total records in CSV: {results.total}</Typography>
            <Typography variant="body2" color="success.main">• New products: {results.new}</Typography>
            <Typography variant="body2" color="warning.main">• Duplicates found: {results.duplicates}</Typography>
            <Typography variant="body2" color="error.main">• Invalid products: {results.invalid}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
              Will import: {productsToImport.length} products
            </Typography>
          </Alert>
        )}

        {results && results.new > 0 && !results.completed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">New Products to Import:</Typography>
            <Box sx={{ maxHeight: expandedSections.newProducts ? 'none' : 150, overflow: 'auto', mt: 1 }}>
              {(expandedSections.newProducts ? results.newProducts : results.newProducts.slice(0, 10)).map((item, index) => (
                <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Row {item.rowIndex}: "{item.product.name}" - RM{item.product.price}
                  {item.product.category_id && ` (${categories?.find(c => c.id === item.product.category_id)?.name || item.product.category_id})`}
                  {item.product.barcode && ` | Barcode: ${item.product.barcode}`}
                </Typography>
              ))}
            </Box>
            {results.newProducts.length > 10 && (
              <Button
                size="small"
                onClick={() => setExpandedSections(prev => ({ ...prev, newProducts: !prev.newProducts }))}
                sx={{ mt: 1 }}
              >
                {expandedSections.newProducts ? 'Show Less' : `Show ${results.newProducts.length - 10} More`}
              </Button>
            )}
          </Alert>
        )}

        {results && results.duplicates > 0 && !results.completed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Duplicate Products Found:</Typography>
            <Box sx={{ maxHeight: expandedSections.duplicateProducts ? 'none' : 150, overflow: 'auto', mt: 1 }}>
              {(expandedSections.duplicateProducts ? results.duplicateProducts : results.duplicateProducts.slice(0, 10)).map((item, index) => (
                <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Row {item.rowIndex}: "{item.product.name}"
                  {item.product.barcode && ` (${item.product.barcode})`}
                  <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}>
                    → Matches existing: "{item.existingProduct.name}"
                  </Typography>
                </Typography>
              ))}
            </Box>
            {results.duplicateProducts.length > 10 && (
              <Button
                size="small"
                onClick={() => setExpandedSections(prev => ({ ...prev, duplicateProducts: !prev.duplicateProducts }))}
                sx={{ mt: 1 }}
              >
                {expandedSections.duplicateProducts ? 'Show Less' : `Show ${results.duplicateProducts.length - 10} More`}
              </Button>
            )}

            <Box sx={{ mt: 2 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">How to handle {results.duplicates} duplicates?</FormLabel>
                <RadioGroup
                  row
                  value={duplicateHandling}
                  onChange={handleDuplicateHandlingChange}
                >
                  <FormControlLabel value="skip" control={<Radio />} label="Skip duplicates (recommended)" />
                  <FormControlLabel value="overwrite" control={<Radio />} label="Overwrite duplicates" />
                </RadioGroup>
              </FormControl>
            </Box>
          </Alert>
        )}

        {results && results.invalid > 0 && !results.completed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Invalid Products:</Typography>
            <Box sx={{ maxHeight: expandedSections.invalidProducts ? 'none' : 150, overflow: 'auto', mt: 1 }}>
              {(expandedSections.invalidProducts ? results.invalidProducts : results.invalidProducts.slice(0, 5)).map((item, index) => (
                <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Row {item.rowIndex}: {item.error}
                  {item.product.name && (
                    <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}>
                      (Product: "{item.product.name}")
                    </Typography>
                  )}
                </Typography>
              ))}
            </Box>
            {results.invalidProducts.length > 5 && (
              <Button
                size="small"
                onClick={() => setExpandedSections(prev => ({ ...prev, invalidProducts: !prev.invalidProducts }))}
                sx={{ mt: 1 }}
              >
                {expandedSections.invalidProducts ? 'Show Less' : `Show ${results.invalidProducts.length - 5} More`}
              </Button>
            )}
          </Alert>
        )}

        {showImportResults && (
          <Alert severity={results.importFailed === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Import Results:</Typography>
            <Typography variant="body2">• Products successfully imported: {results.imported}</Typography>
            {results.importFailed > 0 && (
              <Typography variant="body2">• Failed imports: {results.importFailed}</Typography>
            )}
            
            {results.importErrors && results.importErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="error">Import errors:</Typography>
                <pre style={{ fontSize: "0.75rem", margin: 0 }}>
                  {results.importErrors.join("\n")}
                </pre>
              </Box>
            )}
          </Alert>
        )}

        {canProceedToImport && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>Confirm Import</Typography>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              Ready to import {productsToImport.length} products:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="success.main">New Products: {results.new}</Typography>
                {duplicateHandling === "overwrite" && results.duplicates > 0 && (
                  <Typography variant="subtitle2" color="warning.main">Duplicates to Overwrite: {results.duplicates}</Typography>
                )}
                {duplicateHandling === "skip" && results.duplicates > 0 && (
                  <Typography variant="subtitle2" color="info.main">Duplicates to Skip: {results.duplicates}</Typography>
                )}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                />
              }
              label={`I confirm importing ${productsToImport.length} products into the database`}
            />
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>
              {previewMode ? "Analyzing products..." : "Importing products..."}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {showImportResults ? "Close" : "Cancel"}
        </Button>
        
        {previewMode && (
          <Button
            onClick={handlePreview}
            variant="contained"
            startIcon={<Preview />}
            disabled={!file || loading}
          >
            {loading ? "Analyzing..." : "Preview Import"}
          </Button>
        )}
        
        {canProceedToImport && (
          <Button
            onClick={handleImport}
            variant="contained"
            color="primary"
            disabled={loading || !confirmationChecked}
            startIcon={<Upload />}
          >
            {loading ? "Importing..." : `Import ${productsToImport.length} Products`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ProductImport;